import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, X, Send, Bot, Sparkles, ArrowRight } from 'lucide-react';

import { SERVICES } from '../../constants/serviceConstants';

export const GeminiConcierge = ({ isOpen, onClose, onViewService }) => {
  const [messages, setMessages] = useState([
    {
      id: 'welcome',
      role: 'model',
      text: "Hello! I'm your ParrotOffice AI Concierge. I can help you find the perfect digital service for your project. What are you looking to achieve today?"
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isOpen]);

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMsg = {
      id: Date.now().toString(),
      role: 'user',
      text: input
    };

    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);

    try {
      const recommendedIds = 'text';
      
      const modelMsg = {
        id: (Date.now() + 1).toString(),
        role: 'model',
        text: recommendedIds.length > 0 
          ? "I found some services that match your needs perfectly:" 
          : "I couldn't find an exact match, but here is our full catalog or try rephrasing your request.",
        relatedServiceIds: recommendedIds
      };
      
      setMessages(prev => [...prev, modelMsg]);
    } catch (e) {
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        role: 'model',
        text: "I'm having trouble connecting to my brain right now. Please try again later."
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed bottom-24 right-6 w-96 h-[500px] bg-white rounded-2xl shadow-2xl border border-indigo-100 flex flex-col z-50 overflow-hidden animate-fade-in-up">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-4 flex justify-between items-center text-white">
        <div className="flex items-center gap-2">
          <div className="bg-white/20 p-1.5 rounded-full">
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-bold text-sm">Parrot Concierge</h3>
            <p className="text-xs text-indigo-100">Powered by Gemini AI</p>
          </div>
        </div>
        <button onClick={onClose} className="hover:bg-white/20 p-1 rounded-full transition-colors">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 bg-slate-50 space-y-4">
        {messages.map((msg) => (
          <div key={msg.id} className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
            <div className={`max-w-[85%] p-3 rounded-2xl text-sm ${
              msg.role === 'user' 
                ? 'bg-indigo-600 text-white rounded-tr-none' 
                : 'bg-white text-slate-700 shadow-sm border border-slate-100 rounded-tl-none'
            }`}>
              {msg.text}
            </div>

            {/* Render Recommendations if any */}
            {msg.relatedServiceIds && msg.relatedServiceIds.length > 0 && (
              <div className="mt-2 flex flex-col gap-2 w-[85%]">
                {msg.relatedServiceIds.map(id => {
                  const service = SERVICES.find(s => s.id === id);
                  if (!service) return null;
                  return (
                    <div 
                      key={id} 
                      onClick={() => onViewService(id)}
                      className="bg-white p-2 rounded-lg border border-indigo-100 shadow-sm hover:border-indigo-300 cursor-pointer flex gap-3 items-center transition-all"
                    >
                      <img src={service.imageUrl} className="w-10 h-10 rounded-md object-cover" alt="" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold text-slate-800 truncate">{service.title}</p>
                        <p className="text-[10px] text-slate-500 truncate">{service.category}</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-indigo-400" />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
        {isLoading && (
          <div className="flex items-center gap-2 text-slate-400 text-xs ml-2">
            <Bot className="w-4 h-4 animate-bounce" />
            <span>Thinking...</span>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-3 bg-white border-t border-slate-100">
        <div className="flex gap-2">
          <input
            type="text"
            className="flex-1 bg-slate-100 border-0 rounded-full px-4 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all outline-none"
            placeholder="Ask for a service..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
          />
          <button 
            onClick={handleSend}
            disabled={!input.trim() || isLoading}
            className="bg-indigo-600 text-white p-2 rounded-full hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};